import React, { forwardRef, useState } from 'react';
import { css, cx } from '../../../customEmotion';
import MuiAccordion from '@material-ui/core/Accordion';
import MuiAccordionSummary from '@material-ui/core/AccordionSummary';
import MuiAccordionDetails from '@material-ui/core/AccordionDetails';
import { withStyles } from '@material-ui/core/styles';

import { useCustomStyle } from './NestedAccordionSelectItem';
import Checkbox from '../../Checkbox';
import MenuItem from '../../MenuItem';

import Icon from '../../Icon';
import { useMUITheme } from '../../styles/theme';

const Accordion = withStyles({
  root: {
    borderTop: 'none',
    boxShadow: 'none',
    minHeight: 0,
    '&$expanded': {
      minHeight: 0,
    },
  },
  expanded: {},
})(MuiAccordion);

const AccordionSummary = withStyles(theme => ({
  root: {
    position: 'relative',
    flexDirection: 'row-reverse',
    padding: 0,
    minHeight: 0,
    transition: 'background-color 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
    '&$expanded': {
      margin: 0,
      minHeight: 0,
    },
    '&:hover': {
      backgroundColor: theme.palette.primary.lightest,
    },
    '&.selected': {
      backgroundColor: theme.palette.primary.lightest,
    },
  },
  content: {
    margin: 0,
    transition: 'background-color 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
    '&$expanded': {
      margin: 0,
      minHeight: 0,
    },
  },
  expanded: {},
  expandIcon: {
    position: 'absolute',
    left: 0,
    marginLeft: '8px',
    padding: 0,
    color: theme.palette.grey[500],
    width: '16px',
    height: '16px',
  },
}))(MuiAccordionSummary);

const AccordionDetails = withStyles({
  root: {
    padding: 0,
  },
})(MuiAccordionDetails);

export interface OptionData {
  value: string;
  text: string;
  defaultOpen?: boolean;
  children?: OptionData[];
}

interface NestedAccordionSelectGroupProps {
  option: OptionData;
  handleChangeValue: (value: string | string[]) => void;
  selectedValue: string[];
}

const getSelectedChildCount = (
  children: OptionData[] = [],
  selectedValue: string[]
): number => {
  return children.reduce((count, child) => {
    if (selectedValue.includes(child.value)) {
      count++;
    }
    // 遞迴檢查子項的子項
    if (child.children) {
      count += getSelectedChildCount(child.children, selectedValue);
    }
    return count;
  }, 0);
};

const getAllChildrenValues = (option: OptionData): string[] => {
  const values: string[] = [];
  if (option.children) {
    option.children.forEach(child => {
      values.push(child.value);
      const childValues = getAllChildrenValues(child);
      childValues.forEach(value => values.push(value));
    });
  }
  return values;
};

const NestedAccordionSelectGroup = forwardRef<
  HTMLDivElement,
  NestedAccordionSelectGroupProps
>(({ option, handleChangeValue, selectedValue }, ref) => {
  const theme = useMUITheme();
  const customOptionStyle = useCustomStyle(theme);
  const [expanded, setExpanded] = useState(option.defaultOpen || false);

  const hasChildren = option.children && option.children.length > 0;
  const allChildrenValues = React.useMemo(() => getAllChildrenValues(option), [option]);
  const selectedChildCount = React.useMemo(
    () =>
      hasChildren ? getSelectedChildCount(option.children, selectedValue) : 0,
    [hasChildren, option.children, selectedValue]
  );
  const totalChildCount = React.useMemo(
    () => (hasChildren ? allChildrenValues.length : 0),
    [hasChildren, allChildrenValues]
  );

  const isSelected = selectedValue.includes(option.value);
  const isAllChildrenSelected = hasChildren && selectedChildCount === totalChildCount;

  // 監聽子項選中狀態，自動更新父項選中狀態
  React.useEffect(() => {
    if (hasChildren && isAllChildrenSelected && !isSelected) {
      // 當所有子項被選中但父項未被選中時，自動選中父項
      handleChangeValue(selectedValue.concat([option.value]));
    } else if (hasChildren && !isAllChildrenSelected && isSelected) {
      // 當不是所有子項都被選中但父項被選中時，取消選中父項
      handleChangeValue(selectedValue.filter(v => v !== option.value));
    }
  }, [hasChildren, isAllChildrenSelected, isSelected, selectedValue, option.value, handleChangeValue]);

  const handleOptionChange = React.useCallback(() => {
    const isCurrentlySelected = selectedValue.includes(option.value);
    
    if (isCurrentlySelected) {
      // 如果目前是選中狀態，則取消選中該選項及其所有子項
      const newValue = selectedValue.filter(
        v => v !== option.value && !allChildrenValues.includes(v)
      );
      handleChangeValue(newValue);
    } else {
      // 如果目前是未選中狀態
      if (isAllChildrenSelected) {
        // 如果所有子項都已經被選中，則只選中父選項
        const newValue = selectedValue.concat([option.value]);
        handleChangeValue(newValue);
      } else if (selectedChildCount === 0) {
        // 如果所有子項都是未選中狀態，則選中該選項及其所有子項
        const newValue = selectedValue.concat([option.value]).concat(allChildrenValues);
        handleChangeValue(newValue);
      } else {
        // 如果有部分子項被選中，則選中該選項及其所有子項
        const newValue = selectedValue.concat([option.value]).concat(allChildrenValues);
        handleChangeValue(newValue);
      }
    }
  }, [option, selectedValue, selectedChildCount, allChildrenValues, isAllChildrenSelected, handleChangeValue]);

  const handleExpand = React.useCallback(
    (e: React.MouseEvent) => {
      if (!hasChildren) return;
      e.stopPropagation();
      setExpanded(!expanded);
      if (window) {
        setTimeout(() => {
          window.dispatchEvent(new Event('resize'));
        }, 100);
      }
    },
    [hasChildren, expanded]
  );

  return (
    <Accordion ref={ref} expanded={expanded}>
      <AccordionSummary
        expandIcon={hasChildren && <Icon name="ExpandMore" />}
        className={cx({ selected: isSelected })}
        IconButtonProps={{
          onClick: handleExpand,
        }}
        onClick={handleOptionChange}
      >
        <MenuItem
          className={cx(
            customOptionStyle.container,
            {
              ['Mui-selected']: isSelected,
            },
            css`
              width: 100%;
              padding: 8px 8px 8px 24px;
            `
          )}
          value={option.value}
          selected={isSelected}
        >
          <Checkbox
            color="primary"
            checked={Boolean(isSelected || isAllChildrenSelected)}
            indeterminate={
              !!hasChildren &&
              selectedChildCount > 0 &&
              selectedChildCount < totalChildCount &&
              !isAllChildrenSelected
            }
            onClick={handleOptionChange}
            className={customOptionStyle.checkbox}
          />
          <div className={customOptionStyle.greyBlock}>{option.text}</div>
        </MenuItem>
      </AccordionSummary>
      <AccordionDetails>
        {option.children &&
          option.children.map((child) => (
            <div
              key={child.value}
              className={css`
                padding-left: 24px;
              `}
            >
              <NestedAccordionSelectGroup
                option={child}
                handleChangeValue={handleChangeValue}
                selectedValue={selectedValue}
              />
            </div>
          ))}
      </AccordionDetails>
    </Accordion>
  );
});

export default NestedAccordionSelectGroup;
